import { useEffect } from "react";
import { IFormSection } from "../../../types/common";
import useGet from "../../common/useGet";
import {
  API_SETTLEMENT_BENEFICIARIES,
  API_SETTLEMENT_RULES,
} from "../../../utils/apis";
import {
  createActivateAction,
  createColumn,
  createDeactivateAction,
  createViewEdit,
  formatCurrency,
  formatDateTime,
  formatTime,
  getForm,
  getRegister,
  returnObject,
} from "../../../utils/helpers";
import { URL_SETTLEMENT_RULES } from "../../../utils/urls";
import { FormActions } from "../../../components/common/FormActions";
import { BooleanTags } from "../../../components/common/registerUtils";
import { BOOLEAN_SELECTIONS } from "../../../utils/constants";

const url = URL_SETTLEMENT_RULES;

export const useSettlementRules = () => {
  const tableColumns = () => [
    createColumn("beneficiary_type", true, "Beneficiary Type"),
    createColumn("beneficiary", true, "Beneficiary"),
    createColumn("settlement_delay_days", true, "Delay Days"),
    createColumn("settlement_timing", true, "Timing"),
    createColumn("auto_settlement_enabled", true, "Auto Settlement"),
    createColumn("is_active", true, "Active"),
    createColumn("configured_by", true, "Configured By"),
    createColumn("configured_at", true, "Configured At"),
    createColumn("actions", false),
  ];

  const tableData = (data: any[] = []) => {
    if (!Array.isArray(data)) return [];

    return data.map((item) => {
      return {
        ...item,
        beneficiary: item?.beneficiary?.name,
        settlement_delay_days: item?.settlement_delay_days || "0",
        auto_settlement_enabled: (
          <BooleanTags status={item?.auto_settlement_enabled} />
        ),
        configured_by: item?.configured_by?.name,
        is_active: <BooleanTags status={item?.is_active} />,
        configured_at: formatDateTime(item?.configured_at),
        actions: (
          <FormActions
            actions={[
              ...createViewEdit(url, item?.id),
              ...returnObject(item?.is_active, [
                createDeactivateAction(
                  `${API_SETTLEMENT_RULES}${item?.id}/disable/`,
                  item?.beneficiary?.name,
                  {},
                  {
                    showReasonField: true,
                    method: "POST",
                  },
                ),
              ]),
              ...returnObject(!item?.is_active, [
                createActivateAction(
                  `${API_SETTLEMENT_RULES}${item?.id}/`,
                  item?.beneficiary?.name,
                  {
                    is_active: true,
                  },
                  {
                    method: "PATCH",
                  },
                ),
              ]),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    return {
      sections: [
        {
          title: "Settlement Rule Details",
          fields: {
            beneficiary_type: item?.beneficiary_type,
            beneficiary: item?.beneficiary?.name,
            settlement_timing: item?.settlement_timing,
            settlement_delay_days: item?.settlement_delay_days,
            approval_threshold_amount: formatCurrency(
              item?.approval_threshold_amount,
            ),
            max_batch_amount: formatCurrency(item?.max_batch_amount),
            settlement_window_start: formatTime(item?.settlement_window_start),
            settlement_window_end: formatTime(item?.settlement_window_end),
            auto_settlement_enabled: (
              <BooleanTags status={item?.auto_settlement_enabled} />
            ),
            is_active: <BooleanTags status={item?.is_active} />,
            configured_at: formatDateTime(item?.configured_at),
          },
        },
      ],
    };
  };

  const formRoute = getForm(url);

  return {
    tableColumns,
    tableData,
    viewData,
    formRoute,
  };
};

export const useSettlementRulesForm = () => {
  const {
    data: beneficiaries,
    loading: beneficiariesLoading,
    error: beneficiariesError,
    loadData: loadBeneficiaries,
  } = useGet();

  useEffect(() => {
    loadBeneficiaries({ api: API_SETTLEMENT_BENEFICIARIES });
  }, []);

  const sections: IFormSection[] = [
    {
      title: "Settlement Rules",
      fields: [
        {
          field_name: "beneficiary",
          label: "Beneficiary",
          type: "select",
          options_data: beneficiaries?.results?.map((item: any) => ({
            id: item.id,
            name: item.name,
          })),

          props: { required: true, placeholder: "Select Beneficiary" },
        },

        {
          field_name: "settlement_delay_days",
          label: "Settlement Delay Days",
          type: "decimal",
          props: { required: true, min: 0 },
        },
        {
          field_name: "approval_threshold_amount",
          label: "Approval Threshold Amount",
          type: "decimal",
          props: { required: true, min: 0, step: "0.01" },
        },
        {
          field_name: "max_batch_amount",
          label: "Max Batch Amount",
          type: "decimal",
          props: { required: true, min: 0, step: "0.01" },
        },
        {
          field_name: "settlement_window",
          label: "Settlement Window",
          type: "time_range",
          props: {
            required: false,
            startField: "settlement_window_start",
            endField: "settlement_window_end",
          },
        },
        {
          field_name: "auto_settlement_enabled",
          label: "Enable Auto Settlement",
          type: "select",
          options_data: BOOLEAN_SELECTIONS,
          props: { required: true },
        },
        {
          field_name: "is_active",
          label: "Is Active",
          type: "select",
          options_data: BOOLEAN_SELECTIONS,
          props: { required: true },
        },
      ],
    },
  ];

  const redirect = getRegister(url);

  function onSubmit(payload: any, id: string | number) {
    const cleanedPayload: any = {};

    Object.keys(payload).forEach((key) => {
      if (!key.endsWith("_name") && key !== "settlement_window") {
        cleanedPayload[key] = payload[key];
      }
    });

    if (
      !cleanedPayload.settlement_window_start ||
      !cleanedPayload.settlement_window_end
    ) {
      throw new Error("Settlement window start and end times are required");
    }

    return {
      id,
      api: API_SETTLEMENT_RULES,
      params: cleanedPayload,
    };
  }

  return {
    sections,
    beneficiariesLoading,
    beneficiariesError,
    redirect,
    onSubmit,
    defaultValues: {
      auto_settlement_enabled: true,
      is_active: true,
      settlement_delay_days: 0,
    },
  };
};
